/**
 * Caravan "Recently viewed" tracker + renderer
 * Paired with snippets/cw-recently-viewed.liquid (wave 41)
 *
 * Link from theme.liquid (just before </body>):
 *   {{ 'cw-recently-viewed.js' | asset_url | script_tag }}
 *
 * Two responsibilities:
 *   1. Track — on any product page (detected via window.location.pathname starting
 *      with /products/), push the current handle to a localStorage queue.
 *   2. Render — on any page with a [data-cw-recently-viewed] placeholder, read
 *      the queue, fetch product JSON via /products/{handle}.js, and render tiles.
 *
 * Queue rules:
 *   - Max 12 entries (deduped, FIFO eviction)
 *   - Stored as JSON array of handles in localStorage.cw-recently-viewed
 *   - On render: filter out the current product (the PDP shouldn't recommend itself)
 *
 * Caches product fetches in-memory for the session to avoid re-fetching when
 * multiple placeholders are on the same page (rare but possible).
 *
 * Zero dependencies. ~180 lines.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'cw-recently-viewed';
  var MAX_HISTORY = 12;
  var sessionCache = {};

  // ─── Helpers ─────────────────────────────────────────────────────
  function readQueue() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter(function (h) { return typeof h === 'string' && h; }) : [];
    } catch (e) {
      return [];
    }
  }

  function writeQueue(queue) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
    } catch (e) {
      // Quota exceeded or storage disabled — silent fail
    }
  }

  function pushHandle(handle) {
    if (!handle) return;
    var queue = readQueue();
    // Remove existing occurrence (dedupe — moves to front)
    queue = queue.filter(function (h) { return h !== handle; });
    // Add to front
    queue.unshift(handle);
    // Cap
    if (queue.length > MAX_HISTORY) queue = queue.slice(0, MAX_HISTORY);
    writeQueue(queue);
  }

  function extractProductHandle() {
    // Match /products/<handle> or /products/<handle>?... or /products/<handle>/...
    var match = window.location.pathname.match(/^\/products\/([^\/\?]+)/);
    return match ? match[1] : null;
  }

  function fetchProduct(handle) {
    if (sessionCache[handle]) return Promise.resolve(sessionCache[handle]);
    return fetch('/products/' + handle + '.js')
      .then(function (r) {
        if (!r.ok) return null;
        return r.json();
      })
      .then(function (data) {
        if (data) sessionCache[handle] = data;
        return data;
      })
      .catch(function () { return null; });
  }

  function formatMoney(cents) {
    if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function escapeHtml(s) {
    if (typeof s !== 'string') return '';
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // ─── Render ──────────────────────────────────────────────────────
  function renderTile(product) {
    if (!product) return '';
    var imgUrl = product.featured_image || (product.images && product.images[0]) || '';
    var imgTag = imgUrl ?
      '<img src="' + imgUrl.replace(/\.(jpg|jpeg|png|webp)(\?|$)/i, '_x360.$1$2') +
      '" alt="' + escapeHtml(product.title) + '" loading="lazy" width="300" height="375">' :
      '<div class="cw-rv__img cw-rv__img--ph"></div>';
    var price = formatMoney(product.price);
    var sale = '';
    if (product.compare_at_price && product.compare_at_price > product.price) {
      sale = '<s class="cw-rv__price-was">' + formatMoney(product.compare_at_price) + '</s>';
    }
    return [
      '<li class="cw-rv__tile">',
      '  <a href="/products/' + escapeHtml(product.handle) + '" class="cw-rv__link">',
      '    <div class="cw-rv__media">' + imgTag + '</div>',
      '    <small class="cw-rv__vendor">' + escapeHtml(product.vendor || '') + '</small>',
      '    <strong class="cw-rv__title">' + escapeHtml(product.title) + '</strong>',
      '    <span class="cw-rv__price' + (sale ? ' cw-rv__price--sale' : '') + '">' + price + '</span>',
      sale,
      '  </a>',
      '</li>'
    ].join('');
  }

  function renderPlaceholder(el) {
    var grid = el.querySelector('[data-cw-rv-grid]');
    var empty = el.querySelector('[data-cw-rv-empty]');
    var count = parseInt(el.dataset.cwRvCount, 10) || 6;
    var currentHandle = el.dataset.cwRvCurrentHandle || '';

    var queue = readQueue();
    // Filter the current product so a PDP doesn't recommend itself
    if (currentHandle) {
      queue = queue.filter(function (h) { return h !== currentHandle; });
    }

    if (queue.length === 0) {
      // Empty state — keep placeholder hidden if no copy, else show empty message
      if (empty && empty.textContent.trim()) {
        empty.hidden = false;
        el.hidden = false;
      }
      return;
    }

    var toFetch = queue.slice(0, count);

    Promise.all(toFetch.map(fetchProduct)).then(function (products) {
      var validProducts = products.filter(function (p) { return p && p.available !== false; });
      if (validProducts.length === 0) {
        if (empty) empty.hidden = false;
        el.hidden = false;
        return;
      }
      var html = validProducts.map(renderTile).join('');
      grid.innerHTML = html;
      el.hidden = false;
    });
  }

  // ─── Init ────────────────────────────────────────────────────────
  function init() {
    // 1. Track current product view if we're on a PDP
    var currentHandle = extractProductHandle();
    if (currentHandle) {
      pushHandle(currentHandle);
    }

    // 2. Hydrate any placeholders on the current page
    var placeholders = document.querySelectorAll('[data-cw-recently-viewed]');
    placeholders.forEach(renderPlaceholder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
